import { CalendarClock, Lock, MessageSquare, Users } from "lucide-react";
import { Link } from "wouter";
import type { OutingCardData } from "../lib/queries";
import { ACTIVITY_ICONS, formatDate } from "../lib/format";
import { useT } from "../lib/i18n";
import { thumbUrl } from "../lib/photos";
import { Badge } from "@/components/ui/badge";
import { LikeButton } from "./LikeButton";
import { Avatar } from "./Avatar";
import { Stars } from "./ui";

export function OutingCard({ outing }: { outing: OutingCardData }) {
  const { t, labelizeT } = useT();
  const owner = outing.outingPeopleByOutingId.nodes.find((p) => p.role === "OWNER");
  const participants = outing.outingPeopleByOutingId.nodes.filter((p) => p.role === "PARTICIPANT");
  const routes = outing.outingRoutesByOutingId?.nodes ?? [];
  const thumb = outing.outingPhotos?.nodes[0];
  const ActivityIcon = ACTIVITY_ICONS[outing.activity];

  return (
    <Link
      href={`/outing/${outing.id}`}
      className="block rounded-xl border bg-card p-4 text-card-foreground shadow-card transition-all duration-150 hover:border-muted-foreground/30 hover:shadow-card-hover"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 font-semibold tracking-tight">
            {ActivityIcon && <ActivityIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />}
            {outing.title}
            {outing.visibility === "PRIVATE" && (
              <span title={t("card.privateTitle")} aria-label={t("card.privateAria")}>
                <Lock aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
              </span>
            )}
            {outing.visibility === "FOLLOWERS" && (
              <span title={t("card.followersTitle")} aria-label={t("card.followersAria")}>
                <Users aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
              </span>
            )}
          </h3>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1 text-sm text-muted-foreground">
            {outing.status === "PLANNED" && (
              <Badge
                title={t("card.plannedTitle")}
                className="mr-1 rounded-md bg-amber-100 font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              >
                <CalendarClock aria-hidden className="size-3" />
                {labelizeT("PLANNED")}
              </Badge>
            )}
            {formatDate(outing.date)} · {labelizeT(outing.activity)}
            {owner?.displayName && (
              <span className="inline-flex items-center gap-1">
                · {t("common.by")}
                <Avatar
                  person={{ displayName: owner.displayName, avatarKey: owner.person?.avatarKey }}
                  size="xs"
                />
                {owner.displayName}
              </span>
            )}
            {participants.length > 0 ? ` +${participants.length}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {outing.rating != null && <Stars rating={outing.rating} />}
          {thumb && (
            <img
              src={thumbUrl(thumb.storageKey, 128, 128)}
              alt=""
              loading="lazy"
              className="size-16 rounded-lg border object-cover"
            />
          )}
        </div>
      </div>
      {routes.length > 0 && (
        <p className="mt-2 truncate text-sm text-primary">
          {routes[0]!.route.name}
          {routes.length > 1 && (
            <span className="text-muted-foreground"> {t("card.moreRoutes", { count: routes.length - 1 })}</span>
          )}
        </p>
      )}
      {outing.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{outing.description}</p>}
      {outing.conditions && (
        <Badge variant="secondary" className="mt-2 rounded-md">
          {t("card.conditions", { value: labelizeT(outing.conditions) })}
        </Badge>
      )}
      <div className="mt-3 flex items-center gap-4 border-t pt-2.5">
        <LikeButton
          outingId={outing.id}
          likeCount={outing.likeCount}
          viewerHasLiked={outing.viewerHasLiked}
        />
        <span
          className="inline-flex items-center gap-1.5 text-sm tabular-nums text-muted-foreground"
          aria-label={t(outing.commentCount === 1 ? "card.commentsOne" : "card.commentsOther", { count: outing.commentCount })}
        >
          <MessageSquare aria-hidden className="size-4" />
          {outing.commentCount}
        </span>
      </div>
    </Link>
  );
}
